
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute } from 'vue-router'

// Components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'

// module components
import History from '@/modules/SecurityCheck/components/History/index.vue'

// Types
import { DispatcherCourierT } from '@/core/types/couriers.types'

// Realisation
import { couriers } from '@/core/realisations/couriers.realisation'

// Composable
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

// Validations
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useFile } from '@/common/composable/useFile'

import { formatDateWithTimeFromBackend } from '@/common/helpers/formatDate'

export default defineComponent({
    components: {
        PageTitle,
        Loader,
        TheButton,
        Cheap,
        FileReader,
        History,
    },
    setup() {
        const route = useRoute()
        const { saveFileByURL } = useFile()
        const courier_id = Number(route.params.id as string)

        const notifications = useNotifications()
        const { havePermissions } = useAccount()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            courier: null as DispatcherCourierT | null,
            is_status_change_loading: false,
        })

        const loadCourier = async () => {
            state.courier = null

            try {
                state.is_loading = true

                const response = await couriers.getCourier(courier_id)
                state.courier = response.data

                state.is_failed = false
            } catch (error) {
                state.is_failed = true

                notifications.notifyError({
                    title: 'При получении данных о курьере произошла ошибка',
                    error,
                })
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(loadCourier)

        const isCanSetOnCheckStatus = computed(() => {
            return havePermissions(['couriers-status-in-checkup', 'all-root-permission'])
        })

        const isCourierOnCheck = computed(() => {
            return state.courier && state.courier.status.id === 1
        })

        const isCanSetActiveStatus = computed(() => {
            return havePermissions(['couriers-status-active', 'all-root-permission'])
        })

        const isCourierActive = computed(() => {
            return state.courier && state.courier.status.id === 2
        })

        const isCanSetArchiveStatus = computed(() => {
            return havePermissions(['couriers-status-archived', 'all-root-permission'])
        })

        const isCourierArchive = computed(() => {
            return state.courier && state.courier.status.id === 3
        })

        const isCourierReject = computed(() => {
            return state.courier && state.courier.status.id === 4
        })

        const updateStatus = (status_id: number, status_name: string) => {
            if (!state.courier) return

            state.courier.status.id = status_id
            state.courier.status.title = status_name
        }

        const setOnCheckStatus = async () => {
            if (state.is_status_change_loading || !state.courier) return

            try {
                state.is_status_change_loading = true

                await couriers.setCheckupStatus(state.courier.id)
                updateStatus(1, 'На проверке(СБ)')

                notifications.notify({ title: 'Статус изменен', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При смене статуса произошла ошибка' })
            } finally {
                state.is_status_change_loading = false
            }
        }

        const setActiveStatus = async () => {
            if (state.is_status_change_loading || !state.courier) return

            try {
                state.is_status_change_loading = true

                await couriers.setActiveStatus(state.courier.id)
                updateStatus(2, 'Активный')

                notifications.notify({ title: 'Статус изменен', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При смене статуса произошла ошибка' })
            } finally {
                state.is_status_change_loading = false
            }
        }

        const setArchiveStatus = async () => {
            if (state.is_status_change_loading || !state.courier) return

            try {
                state.is_status_change_loading = true

                await couriers.setArchiveStatus(state.courier.id)
                updateStatus(3, 'В архиве')

                notifications.notify({ title: 'Статус изменен', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При смене статуса произошла ошибка' })
            } finally {
                state.is_status_change_loading = false
            }
        }

        const isRenderActiveStatusButton = computed(
            () => Boolean(state.courier) && !isCourierActive.value && isCanSetActiveStatus.value
        )
        const isRenderOnCheckStatusButton = computed(
            () => Boolean(state.courier) && !isCourierOnCheck.value && isCanSetOnCheckStatus.value
        )
        const isRenderArchiveStatusButton = computed(
            () => Boolean(state.courier) && !isCourierArchive.value && isCanSetArchiveStatus.value
        )

        const upload_state = reactive({
            certificate: [] as File[],
            drive_license: [] as File[],
            is_certificate_loading: false,
            is_drive_license_loading: false,
        })

        const upload_state$ = useVuelidate(
            {
                certificate: { required },
                drive_license: { required },
            },
            upload_state
        )

        const uploadCertificate = async () => {
            upload_state$.value.certificate.$touch()
            if (upload_state$.value.certificate.$invalid) return

            try {
                upload_state.is_certificate_loading = true

                const form_data = new FormData()
                form_data.append('type', '10')

                upload_state.certificate.forEach((certificate) => {
                    form_data.append('files[]', certificate)
                })

                await couriers.uploadDocument(courier_id, form_data)

                notifications.notify({ title: 'Фото удостоверения загружены', type: 'success' })
                loadCourier()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке фото удостоверения произошла ошибка',
                })
            } finally {
                upload_state.is_certificate_loading = false
            }
        }

        const uploadDriverLicense = async () => {
            upload_state$.value.drive_license.$touch()
            if (upload_state$.value.drive_license.$invalid) return

            try {
                upload_state.is_drive_license_loading = true

                const form_data = new FormData()
                form_data.append('type', '11')

                upload_state.drive_license.forEach((drive_license) => {
                    form_data.append('files[]', drive_license)
                })

                await couriers.uploadDocument(courier_id, form_data)

                notifications.notify({ title: 'Фото водительских прав загружены', type: 'success' })
                loadCourier()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке фото водительских прав произошла ошибка',
                })
            } finally {
                upload_state.is_drive_license_loading = false
            }
        }

        const isCanUploadDocuments = computed(
            () =>
                Boolean(state.courier) &&
                havePermissions(['couriers-upload-documents', 'all-root-permission'])
        )

        const isCanReject = computed(() => {
            if (!state.courier) return false
            if (state.courier.status.id !== 1) return false
            if (!havePermissions(['couriers-status-reject', 'all-root-permission'])) return false

            return true
        })

        const reject_state = reactive({
            is_loading: false,
        })

        const rejectCourier = async () => {
            const answer = confirm('Вы действительно хотите отказать курьеру?')
            if (!answer) return

            try {
                reject_state.is_loading = true

                await couriers.rejectCourier(courier_id)

                notifications.notify({ title: 'Курьеру отказано', type: 'success' })
                loadCourier()
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При отказе произошла ошибка' })
            } finally {
                reject_state.is_loading = false
            }
        }

        //

        const companyName = computed(() =>
            state.courier && state.courier.car ? state.courier.car.company.name : '-'
        )

        const isHistory = computed(() => {
            return state.courier && state.courier.iin
        })

        return {
            state,
            courier_id,
            loadCourier,
            isCourierOnCheck,
            isCourierActive,
            isCourierArchive,
            isCourierReject,
            isCanSetOnCheckStatus,
            isCanSetActiveStatus,
            isCanSetArchiveStatus,
            setOnCheckStatus,
            setActiveStatus,
            setArchiveStatus,
            isRenderActiveStatusButton,
            isRenderOnCheckStatusButton,
            isRenderArchiveStatusButton,
            upload_state,
            upload_state$,
            uploadCertificate,
            uploadDriverLicense,
            isCanUploadDocuments,
            isCanReject,
            reject_state,
            rejectCourier,
            saveFileByURL,
            //
            companyName,
            isHistory,
            formatDateWithTimeFromBackend,
        }
    },
})
